import {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {
  actOnApprovalInstance,
  approveInvoiceApproval,
  approveRfqApproval,
  cancelInvoiceApproval,
  cancelRfqApproval,
  getApprovalDocumentAvailableActions,
  getApprovalEligibility,
  getApprovalInstanceByDocument,
  rejectInvoiceApproval,
  rejectRfqApproval,
  ownerApprovePurchaseOrder,
  ownerRejectPurchaseOrder,
  reworkInvoiceApproval,
  reworkRfqApproval,
  submitInvoiceForApproval,
  submitPurchaseOrderForApproval,
  submitRfqForApproval,
} from '../core/_requests'
import {
  ApprovalAvailableActions,
  ApprovalEligibility,
  ApprovalPolicyModuleType,
  ApprovalInstance,
} from '../core/_models'

type Props = {
  moduleType: ApprovalPolicyModuleType
  documentId: number
  title?: string
  onChanged?: () => void | Promise<void>
}

const formatAmount = (amount?: number | null, currency?: string | null) => {
  if (amount == null) {
    return '-'
  }
  return `${currency || ''} ${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim()
}

const formatRate = (rate?: number | null) => {
  if (rate == null || !Number.isFinite(Number(rate))) return null
  return Number(rate).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 10,
  })
}

const approvalMessage = (eligibility: ApprovalEligibility | null) => {
  if (!eligibility) return null

  const reason = String(eligibility.reason || '')
  const documentCurrency = String(eligibility.currency || '').trim().toUpperCase()
  const comparisonCurrency = String(
    eligibility.comparisonCurrency || eligibility.effectiveLimitCurrency || '',
  ).trim().toUpperCase()

  if (reason.startsWith('CURRENCY_CONVERSION_UNAVAILABLE')) {
    return documentCurrency && comparisonCurrency
      ? `Exchange rate is not configured for ${documentCurrency} to ${comparisonCurrency}. Approval cannot be evaluated.`
      : 'Exchange rate is not configured. Approval cannot be evaluated.'
  }
  if (reason.toLowerCase().includes('exceeds the effective approval limit')) {
    const limit = formatAmount(eligibility.effectiveLimit, eligibility.effectiveLimitCurrency)
    const amount = formatAmount(eligibility.documentAmount, eligibility.currency)
    const converted = eligibility.convertedDocumentAmount != null && eligibility.comparisonCurrency
      ? ` (${formatAmount(eligibility.convertedDocumentAmount, eligibility.comparisonCurrency)} after conversion)`
      : ''
    return `Your approval limit is ${limit}, while this purchase order is ${amount}${converted}. This purchase order exceeds your approval limit.`
  }
  if (reason.toLowerCase().includes('no approval limit')) {
    return 'No approval limit is configured for your account/role for this purchase order.'
  }
  if (reason.toLowerCase().includes('no matching approval policy')) {
    return 'No approval policy is configured for this purchase order.'
  }
  if (eligibility.canApprove || eligibility.canReject || eligibility.unlimited) {
    return 'This purchase order is within your approval limit.'
  }
  return reason ? 'Approval is not available for this purchase order.' : null
}

const ApprovalDocumentStatusCard: FC<Props> = ({moduleType, documentId, title, onChanged}) => {
  const [eligibility, setEligibility] = useState<ApprovalEligibility | null>(null)
  const [availableActions, setAvailableActions] = useState<ApprovalAvailableActions | null>(null)
  const [approvalInstance, setApprovalInstance] = useState<ApprovalInstance | null>(null)
  const [loading, setLoading] = useState(false)
  const [actioning, setActioning] = useState(false)
  const [remarks, setRemarks] = useState('')
  const documentCurrency = String(eligibility?.currency || '').trim().toUpperCase()
  const comparisonCurrency = String(
    eligibility?.comparisonCurrency || eligibility?.effectiveLimitCurrency || '',
  ).trim().toUpperCase()
  const isCrossCurrency = Boolean(
    documentCurrency && comparisonCurrency && documentCurrency !== comparisonCurrency,
  )
  const hasConvertedAmount = eligibility?.convertedDocumentAmount != null

  const load = async () => {
    setLoading(true)
    try {
      const [eligibilityData, actionsData, instanceData] = await Promise.all([
        getApprovalEligibility(moduleType, documentId),
        getApprovalDocumentAvailableActions(moduleType, documentId),
        getApprovalInstanceByDocument(moduleType, documentId),
      ])
      setEligibility(eligibilityData)
      setAvailableActions(actionsData)
      setApprovalInstance(instanceData)
    } catch (error: any) {
      toast.error(error.message || 'Failed to load approval state')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [moduleType, documentId])

  const currentStatus = eligibility?.currentStatus || 'UNKNOWN'

  const canSubmit = Boolean(eligibility?.canSubmit)
  const isPurchaseOrder = moduleType === 'PURCHASE_ORDER'
  const isDraft = currentStatus === 'DRAFT'
  const canApprove = Boolean(
    isPurchaseOrder && isDraft
      ? eligibility?.canApprove
      : isPurchaseOrder
        ? availableActions?.canApprove ?? eligibility?.canApprove
        : eligibility?.canApprove,
  )
  const canReject = Boolean(
    isPurchaseOrder && isDraft
      ? eligibility?.canReject
      : isPurchaseOrder
        ? availableActions?.canReject ?? eligibility?.canReject
        : eligibility?.canReject,
  )
  const canRework = Boolean(availableActions?.canRework ?? eligibility?.canRework)
  const canCancel = Boolean(availableActions?.canCancel ?? eligibility?.canCancel)
  const isPending =
    currentStatus === 'PENDING_APPROVAL' ||
    currentStatus === 'PENDING_OWNER_APPROVAL' ||
    currentStatus === 'SUBMITTED'
  const approvableState = isDraft || isPending
  const showSubmit = isPurchaseOrder
    ? isDraft && !canApprove && canSubmit
    : false
  const showApprove = isPurchaseOrder
    ? approvableState && canApprove
    : (currentStatus === 'DRAFT' || currentStatus === 'SUBMITTED') && canApprove
  const showReject = isPurchaseOrder
    ? approvableState && canReject
    : (currentStatus === 'DRAFT' || currentStatus === 'SUBMITTED') && canReject
  const showRework = isPurchaseOrder
    ? isPending && canRework
    : canRework
  const showCancel = isPurchaseOrder
    ? isPending && canCancel
    : canCancel

  const actionLabel = useMemo(() => {
    if (moduleType === 'RFQ') return 'RFQ approval'
    if (moduleType === 'PURCHASE_ORDER') return 'Purchase order approval'
    return 'Invoice approval'
  }, [moduleType])

  const requireApprovalInstance = () => {
    if (approvalInstance?.id) {
      return approvalInstance.id
    }
    throw new Error('No active approval instance found for this purchase order')
  }

  const submit = async () => {
    try {
      setActioning(true)
      if (moduleType === 'RFQ') {
        await submitRfqForApproval(documentId)
      } else if (moduleType === 'PURCHASE_ORDER') {
        await submitPurchaseOrderForApproval(documentId)
      } else {
        await submitInvoiceForApproval(documentId)
      }
      toast.success('Document submitted for approval')
      await load()
      await onChanged?.()
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit document for approval')
    } finally {
      setActioning(false)
    }
  }

  const approve = async () => {
    try {
      setActioning(true)
      if (moduleType === 'RFQ') {
        await approveRfqApproval(documentId, remarks || undefined)
      } else if (moduleType === 'PURCHASE_ORDER') {
        if (isDraft) {
          await ownerApprovePurchaseOrder(documentId, remarks || undefined)
        } else {
          await actOnApprovalInstance(requireApprovalInstance(), 'APPROVE', remarks || undefined)
        }
      } else {
        await approveInvoiceApproval(documentId, remarks || undefined)
      }
      toast.success(`${actionLabel} approved`)
      setRemarks('')
      await load()
      await onChanged?.()
    } catch (error: any) {
      toast.error(error.message || `Failed to approve ${actionLabel}`)
    } finally {
      setActioning(false)
    }
  }

  const reject = async () => {
    try {
      setActioning(true)
      if (moduleType === 'RFQ') {
        await rejectRfqApproval(documentId, remarks || 'Rejected')
      } else if (moduleType === 'PURCHASE_ORDER') {
        if (isDraft) {
          await ownerRejectPurchaseOrder(documentId, remarks || 'Rejected')
        } else {
          await actOnApprovalInstance(requireApprovalInstance(), 'REJECT', remarks || 'Rejected')
        }
      } else {
        await rejectInvoiceApproval(documentId, remarks || 'Rejected')
      }
      toast.success(`${actionLabel} rejected`)
      setRemarks('')
      await load()
      await onChanged?.()
    } catch (error: any) {
      toast.error(error.message || `Failed to reject ${actionLabel}`)
    } finally {
      setActioning(false)
    }
  }

  const rework = async () => {
    try {
      setActioning(true)
      if (moduleType === 'RFQ') {
        await reworkRfqApproval(documentId, remarks || 'Rework requested')
      } else if (moduleType === 'PURCHASE_ORDER') {
        await actOnApprovalInstance(requireApprovalInstance(), 'REWORK', remarks || 'Rework requested')
      } else {
        await reworkInvoiceApproval(documentId, remarks || 'Rework requested')
      }
      toast.success(`${actionLabel} sent for rework`)
      setRemarks('')
      await load()
      await onChanged?.()
    } catch (error: any) {
      toast.error(error.message || `Failed to send ${actionLabel} for rework`)
    } finally {
      setActioning(false)
    }
  }

  const cancel = async () => {
    try {
      setActioning(true)
      if (moduleType === 'RFQ') {
        await cancelRfqApproval(documentId, remarks || undefined)
      } else if (moduleType === 'PURCHASE_ORDER') {
        await actOnApprovalInstance(requireApprovalInstance(), 'CANCEL', remarks || undefined)
      } else {
        await cancelInvoiceApproval(documentId, remarks || undefined)
      }
      toast.success(`${actionLabel} cancelled`)
      setRemarks('')
      await load()
      await onChanged?.()
    } catch (error: any) {
      toast.error(error.message || `Failed to cancel ${actionLabel}`)
    } finally {
      setActioning(false)
    }
  }

  return (
    <div className='border rounded p-4 bg-light'>
      <div className='d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3'>
        <div>
          <h6 className='fw-bold text-dark mb-1'>{title || (moduleType === 'PURCHASE_ORDER' ? 'Purchase Order Approval' : 'Approval Status')}</h6>
          <div className='text-muted fs-7'>
            {eligibility
              ? `${moduleType} #${documentId} is evaluated by the backend eligibility contract.`
              : 'No approval data loaded.'}
          </div>
        </div>
        </div>

      {loading ? (
        <div className='text-muted'>Loading approval state...</div>
      ) : (
        <>
          <div className='row g-3 mb-3'>
            <div className='col-md-6'>
              <strong>Your Approval Limit</strong>
              <div>{eligibility?.unlimited ? 'Unlimited' : formatAmount(
                eligibility?.effectiveLimit,
                eligibility?.effectiveLimitCurrency || eligibility?.currency,
              )}</div>
            </div>
            {!isCrossCurrency ? (
              <div className='col-md-6'>
                <strong>PO Amount</strong>
                <div>{formatAmount(eligibility?.documentAmount, eligibility?.currency)}</div>
              </div>
            ) : (
              <>
                <div className='col-md-6'>
                  <strong>PO Amount (Quotation Currency)</strong>
                  <div>{formatAmount(eligibility?.documentAmount, documentCurrency)}</div>
                </div>
                <div className='col-md-6'>
                  <strong>PO Amount (Company Currency)</strong>
                  <div>{hasConvertedAmount
                    ? formatAmount(eligibility?.convertedDocumentAmount, comparisonCurrency)
                    : 'Unavailable'}</div>
                </div>
                <div className='col-md-6'>
                  <strong>Exchange Rate Used</strong>
                  <div>{formatRate(eligibility?.exchangeRate)
                    ? `1 ${documentCurrency} = ${formatRate(eligibility.exchangeRate)} ${comparisonCurrency}`
                    : 'Not configured'}</div>
                </div>
              </>
            )}
          </div>

          {approvalMessage(eligibility) ? (
            <div className={`alert ${eligibility?.canApprove || eligibility?.canReject ? 'alert-info' : 'alert-warning'} py-2 mb-3 small`}>
              {approvalMessage(eligibility)}
            </div>
          ) : null}

          <div className='mb-3'>
            <label className='form-label'>Remarks</label>
            <textarea
              className='form-control'
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder='Optional remark'
            />
          </div>

          <div className='d-flex gap-2 flex-wrap mb-3'>
            {showSubmit && (
              <button className='btn btn-primary btn-sm' disabled={actioning} onClick={() => void submit()}>
                Submit for Approval
              </button>
            )}
            {showApprove && (
              <button className='btn btn-success btn-sm' disabled={actioning} onClick={() => void approve()}>
                Approve
              </button>
            )}
            {showReject && (
              <button className='btn btn-danger btn-sm' disabled={actioning} onClick={() => void reject()}>
                Reject
              </button>
            )}
            {showRework && (
              <button className='btn btn-warning btn-sm' disabled={actioning} onClick={() => void rework()}>
                Rework
              </button>
            )}
            {showCancel && (
              <button className='btn btn-outline-warning btn-sm' disabled={actioning} onClick={() => void cancel()}>
                Cancel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default ApprovalDocumentStatusCard
